import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Collapse,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import BuildIcon from '@mui/icons-material/Build';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { statusColors } from '@/styles/theme';
import {
  useWorkOrder,
  useStartService,
  usePauseService,
  useResumeService,
  useCompleteService,
  useStartWorkOrder,
  usePauseWorkOrder,
  useResumeWorkOrder,
  useCompleteWorkOrder,
  useStartLaborItem,
  useCompleteLaborItem,
  useUpdateLaborItemStatus,
  type MyServiceTask,
  type BookingServiceLaborItem,
} from '@/hooks/useWorkOrders';
import { useAllLifts } from '@/hooks/useLifts';
import { useNotify } from '@/hooks/useNotify';
import { formatDuration } from '@/utils/duration';
import { WorkOrderTimerControls } from './WorkOrderTimerControls';
import type { TimerAction } from './workOrderTimerState';
import { MileageRequiredDialog } from '@/components/workorders/MileageRequiredDialog';
import { InlineChecklistPanel } from './InlineChecklistPanel';
import { WorkPathTasksPanel } from './WorkPathTasksPanel';
import { MechanicPartsPanel } from '@/components/workorders/MechanicPartsPanel';
import { WorkOrderNotesButton } from '@/components/workorders/WorkOrderNotesButton';
import type { BookingStatus } from '@/types';
import { intlLocale } from '@/utils/locale';

function isMileageRequiredError(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'data' in error &&
    typeof (error as { data: unknown }).data === 'object' &&
    (error as { data: { error?: unknown } }).data !== null &&
    (error as { data: { error?: unknown } }).data.error === 'MILEAGE_REQUIRED'
  );
}

/**
 * The lifecycle routes refuse a start/resume with 409 when the acting user
 * already has an open timer on another work order (#3281). The legacy
 * route sends the token as `reason`, the reviewed route as `code`; both
 * carry `activeWorkOrderId` when the blocking order is known.
 */
function anotherTimerActiveError(error: unknown): { activeWorkOrderId: string | null } | null {
  if (typeof error !== 'object' || error === null || !('data' in error)) return null;
  const data = (error as { data: unknown }).data;
  if (typeof data !== 'object' || data === null) return null;
  const body = data as { reason?: unknown; code?: unknown; activeWorkOrderId?: unknown };
  if (body.reason !== 'another_timer_active' && body.code !== 'another_timer_active') return null;
  return {
    activeWorkOrderId: typeof body.activeWorkOrderId === 'string' ? body.activeWorkOrderId : null,
  };
}

interface WorkOrderGroupCardProps {
  workOrderId: string;
  services: MyServiceTask[];
  expanded: boolean;
  onToggle: () => void;
  /**
   * The person whose day this card belongs to when it is not the viewer's own
   * (#3281). The header timer then starts, pauses and resumes that person's
   * timer instead of the viewer's, and the toasts name them.
   */
  timerOwner?: { id: string; name: string } | null;
}

export function WorkOrderGroupCard({
  workOrderId,
  services,
  expanded,
  onToggle,
  timerOwner = null,
}: WorkOrderGroupCardProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const navigate = useNavigate();
  const notify = useNotify();
  const [mileageDialogOpen, setMileageDialogOpen] = useState(false);
  const [mileageDialogLoading, setMileageDialogLoading] = useState(false);
  const [pendingServiceId, setPendingServiceId] = useState<string | null>(null);
  const [timerBookingServiceId, setTimerBookingServiceId] = useState<string | null>(
    () => (services.length === 1 ? (services[0]?.bookingServiceId ?? null) : null),
  );

  // In-progress work is always kept expanded and cannot be collapsed.
  const hasInProgressService = services.some((s) => s.status === 'in_progress');
  const effectiveExpanded = expanded || hasInProgressService;

  // Lazy load work order detail on expand
  const woQuery = useWorkOrder(effectiveExpanded ? workOrderId : undefined);
  const wo = woQuery.data;

  // Lifts for resolving names
  const liftsQuery = useAllLifts();
  const liftsMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const lift of liftsQuery.data ?? []) {
      map.set(lift.id, lift.name);
    }
    return map;
  }, [liftsQuery.data]);

  // Service mutations
  const startServiceMut = useStartService();
  const pauseServiceMut = usePauseService();
  const resumeServiceMut = useResumeService();
  const completeServiceMut = useCompleteService();
  // Work-order level: these are the ones that write work_order_time_entries.
  const startWoMut = useStartWorkOrder();
  const pauseWoMut = usePauseWorkOrder();
  const resumeWoMut = useResumeWorkOrder();
  const completeWoMut = useCompleteWorkOrder();
  const startLaborItemMut = useStartLaborItem();
  const completeLaborItemMut = useCompleteLaborItem();
  const updateLaborItemStatusMut = useUpdateLaborItemStatus();

  // Derive info from the first service (all share same WO data)
  const first = services[0] as MyServiceTask | undefined;
  const completedCount = services.filter((s) => s.status === 'completed').length;
  const totalCount = services.length;

  const liftName = wo?.assignedLiftId ? liftsMap.get(wo.assignedLiftId) : null;

  // Other mechanics' services (from full WO detail).
  // Exclude services already shown in "my work" so the viewed mechanic's own
  // services don't duplicate when an admin views another mechanic's day.
  const myServiceIds = useMemo(
    () => new Set(services.map((s) => s.bookingServiceId)),
    [services],
  );
  const otherServices = useMemo(() => {
    if (!wo) return [];
    return wo.services.filter(
      (s) => s.assignedMechanicId && !myServiceIds.has(s.bookingServiceId),
    );
  }, [wo, myServiceIds]);

  if (!first) return null;

  // "Toiminto epäonnistui" alone sent JV's staff back to the work-order page
  // (training video 7.10.2026, 22:14) when the real cause was their own
  // timer left open elsewhere — name it and offer the blocking order.
  const reportActionError = (error: unknown) => {
    const blocked = anotherTimerActiveError(error);
    if (!blocked) {
      notify.error(describeApiError(error, tc, { fallback: t('serviceActions.serviceActionFailed') }));
      return;
    }
    const blockingId = blocked.activeWorkOrderId;
    notify.error(
      timerOwner
        ? t('serviceActions.anotherTimerActiveFor', { name: timerOwner.name })
        : t('serviceActions.anotherTimerActive'),
      t('serviceActions.anotherTimerActiveHint'),
      blockingId
        ? {
            duration: 12000,
            action: {
              label: t('serviceActions.openBlockingWorkOrder'),
              onClick: () => navigate(`/workorders/${blockingId}`),
            },
          }
        : undefined,
    );
  };

  const handleServiceAction = async (action: string, svc: MyServiceTask) => {
    try {
      const params = { workOrderId: svc.workOrderId, serviceId: svc.bookingServiceId };
      switch (action) {
        case 'start':
          await startServiceMut.mutateAsync(params);
          notify.success(t('serviceActions.serviceStarted'));
          break;
        case 'pause':
          await pauseServiceMut.mutateAsync(params);
          notify.success(t('serviceActions.servicePaused'));
          break;
        case 'resume':
          await resumeServiceMut.mutateAsync(params);
          notify.success(t('serviceActions.serviceResumed'));
          break;
        case 'complete':
          await completeServiceMut.mutateAsync(params);
          notify.success(t('serviceActions.serviceCompleted'));
          break;
      }
    } catch (error) {
      if (action === 'complete' && isMileageRequiredError(error)) {
        setPendingServiceId(svc.bookingServiceId);
        setMileageDialogOpen(true);
        return;
      }
      reportActionError(error);
    }
  };

  /**
   * The header timer (#feedback). Unlike the per-service buttons these hit the
   * work-order routes, so the clock actually starts and stops — and they move
   * every service on the order in the same transaction, which is what a
   * mechanic means by "I started on this car".
   */
  const handleTimerAction = async (action: TimerAction) => {
    // Viewing someone else's day sends their id so the clock is theirs (#3281).
    const onBehalf = timerOwner ? { mechanicId: timerOwner.id } : {};
    try {
      switch (action) {
        case 'start':
          await startWoMut.mutateAsync({
            id: workOrderId,
            bookingServiceId: timerBookingServiceId,
            ...onBehalf,
          });
          notify.success(
            timerOwner
              ? t('serviceActions.timerStartedFor', { name: timerOwner.name })
              : t('serviceActions.serviceStarted'),
          );
          break;
        case 'pause':
          await pauseWoMut.mutateAsync({ id: workOrderId, ...onBehalf });
          notify.success(
            timerOwner
              ? t('serviceActions.timerPausedFor', { name: timerOwner.name })
              : t('serviceActions.servicePaused'),
          );
          break;
        case 'resume':
          await resumeWoMut.mutateAsync({
            id: workOrderId,
            bookingServiceId: timerBookingServiceId,
            ...onBehalf,
          });
          notify.success(
            timerOwner
              ? t('serviceActions.timerStartedFor', { name: timerOwner.name })
              : t('serviceActions.serviceResumed'),
          );
          break;
        case 'complete':
          await completeWoMut.mutateAsync({ id: workOrderId });
          notify.success(t('serviceActions.serviceCompleted'));
          break;
      }
    } catch (error) {
      // Same reuse as the per-service path: the dialog collects the reading
      // and retries. `pendingServiceId` stays null, which marks this as the
      // work-order completion rather than a single service.
      if (action === 'complete' && isMileageRequiredError(error)) {
        setMileageDialogOpen(true);
        return;
      }
      reportActionError(error);
    }
  };

  const handleMileageConfirm = async (mileage: number) => {
    setMileageDialogLoading(true);
    try {
      // No pending service means the header timer asked to finish the whole
      // order; the dialog is shared rather than duplicated.
      if (pendingServiceId === null) {
        await completeWoMut.mutateAsync({ id: workOrderId, mileage });
        notify.success(t('serviceActions.serviceCompleted'));
        setMileageDialogOpen(false);
        return;
      }
      await completeServiceMut.mutateAsync({
        workOrderId,
        serviceId: pendingServiceId,
        mileage,
      });
      notify.success(t('serviceActions.serviceCompleted'));
      setMileageDialogOpen(false);
      setPendingServiceId(null);
    } catch (error) {
      notify.error(describeApiError(error, tc, { fallback: t('serviceActions.serviceActionFailed') }));
    } finally {
      setMileageDialogLoading(false);
    }
  };

  const handleMileageCancel = () => {
    setMileageDialogOpen(false);
    setPendingServiceId(null);
  };

  /**
   * The work order's own state can forbid labor-item status commands: before
   * the vehicle has arrived, and once it is billing-locked or terminal. The API
   * enforces these rules; this just stops offering an action that would 400
   * (#1028).
   */
  const isLaborItemLocked = (workOrderStatus: string) =>
    ['quote', 'pending', 'confirmed', 'quote_rejected', 'ready_to_invoice', 'invoiced', 'completed', 'cancelled']
      .includes(workOrderStatus);

  const handleLaborItemToggle = async (
    serviceId: string,
    item: BookingServiceLaborItem,
  ) => {
    try {
      const params = { workOrderId, serviceId, itemId: item.id };
      if (item.status === 'completed') {
        // Reopening puts the step — and its parent service — back under way
        // (#1028). My Tasks follows the same rules as the work order view.
        await updateLaborItemStatusMut.mutateAsync({ ...params, status: 'in_progress' });
        notify.success(t('laborItemReopened'));
      } else if (item.status === 'pending') {
        await startLaborItemMut.mutateAsync(params);
        notify.success(t('laborItemStarted'));
      } else if (item.status === 'in_progress') {
        await completeLaborItemMut.mutateAsync(params);
        notify.success(t('laborItemCompleted'));
      }
    } catch (error) {
      notify.error(describeApiError(error, tc, { fallback: t('myTasks.actionFailed') }));
    }
  };

  const renderServiceActions = (svc: MyServiceTask) => {
    const notArrived = ['quote', 'pending', 'confirmed', 'quote_rejected'].includes(
      svc.workOrderStatus,
    );

    return (
      <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
        {svc.status === 'pending' &&
          (notArrived ? (
            <Tooltip title={t('serviceActions.vehicleNotArrived')}>
              <span>
                <IconButton
                  disabled
                  size="small"
                  aria-label={t('serviceActions.vehicleNotArrived')}
                >
                  <PlayArrowIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title={t('serviceActions.startService')}>
              <IconButton
                size="small"
                color="primary"
                aria-label={t('serviceActions.startService')}
                onClick={() => void handleServiceAction('start', svc)}
              >
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ))}
        {svc.status === 'in_progress' && (
          <>
            <Tooltip title={t('serviceActions.pauseService')}>
              <IconButton
                size="small"
                aria-label={t('serviceActions.pauseService')}
                onClick={() => void handleServiceAction('pause', svc)}
              >
                <PauseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('serviceActions.completeService')}>
              <IconButton
                size="small"
                color="success"
                aria-label={t('serviceActions.completeService')}
                onClick={() => void handleServiceAction('complete', svc)}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        {svc.status === 'paused' && (
          <>
            <Tooltip title={t('serviceActions.resumeService')}>
              <IconButton
                size="small"
                color="primary"
                aria-label={t('serviceActions.resumeService')}
                onClick={() => void handleServiceAction('resume', svc)}
              >
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('serviceActions.completeService')}>
              <IconButton
                size="small"
                color="success"
                aria-label={t('serviceActions.completeService')}
                onClick={() => void handleServiceAction('complete', svc)}
              >
                <CheckCircleIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>
    );
  };

  // Find matching WO service detail for labor items / parts
  const getServiceDetail = (bookingServiceId: string) =>
    wo?.services.find((s) => s.bookingServiceId === bookingServiceId);

  return (
    <>
    <Card sx={{ mb: 1.5 }}>
      <CardContent
        sx={{
          py: 1.5,
          px: 2,
          '&:last-child': { pb: 1.5 },
          ...(hasInProgressService ? {} : { cursor: 'pointer' }),
        }}
        {...(hasInProgressService ? {} : { onClick: onToggle })}
      >
        {/* Collapsed header */}
        <Stack direction="row" alignItems="flex-start" spacing={1}>
          <BuildIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={600} noWrap>
              {first.vehicleRegNumber} · {first.vehicleMakeModel}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {first.customerName}
            </Typography>
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ mt: 0.5 }}
              flexWrap="wrap"
              useFlexGap
            >
              {first.scheduledAt && (
                <Chip
                  size="small"
                  icon={<AccessTimeIcon />}
                  label={new Date(first.scheduledAt).toLocaleTimeString(intlLocale(), {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                  variant="outlined"
                />
              )}
              <Chip
                size="small"
                label={t('myTasks.servicesCount', {
                  completed: completedCount,
                  total: totalCount,
                })}
                color={completedCount === totalCount ? 'success' : 'default'}
                variant="outlined"
              />
              {wo?.customerWaits && (
                <Chip
                  size="small"
                  icon={<HourglassEmptyIcon />}
                  label={t('myTasks.customerWaits')}
                  color="warning"
                  variant="outlined"
                />
              )}
            </Stack>
            {/* Inline service summary */}
            <Stack
              direction="row"
              spacing={0.5}
              sx={{ mt: 0.5 }}
              flexWrap="wrap"
              useFlexGap
            >
              {services.map((svc) => (
                <Chip
                  key={svc.bookingServiceId}
                  size="small"
                  label={svc.serviceName}
                  sx={{
                    backgroundColor: statusColors[svc.status] ?? statusColors.pending,
                    color: 'common.white',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                  }}
                />
              ))}
            </Stack>
          </Box>
          {/*
            Start / pause / finish at the top of the card, visible while it is
            collapsed. Mechanics reported these were buried on the service
            rows, where they were both too small for a tablet and — worse —
            wired to the per-service routes that leave the clock stopped.
          */}
          {first && (
            <WorkOrderTimerControls
              // Prefer the detail query once the card is expanded so a start
              // that has already invalidated the work order is reflected before
              // my-services refetches. Collapsed cards still use the list row.
              status={(wo?.status ?? first.workOrderStatus) as BookingStatus}
              onAction={(action) => void handleTimerAction(action)}
              busy={
                startWoMut.isPending ||
                pauseWoMut.isPending ||
                resumeWoMut.isPending ||
                completeWoMut.isPending
              }
              services={services.map((svc) => ({
                bookingServiceId: svc.bookingServiceId,
                name: svc.serviceName,
              }))}
              bookingServiceId={timerBookingServiceId}
              onBookingServiceIdChange={setTimerBookingServiceId}
            />
          )}
          <WorkOrderNotesButton bookingId={workOrderId} />
          {!hasInProgressService && (
            <Tooltip title={expanded ? t('myTasks.collapse') : t('myTasks.expand')}>
              <IconButton
                size="small"
                aria-label={expanded ? t('myTasks.collapse') : t('myTasks.expand')}
                sx={{
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle();
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </CardContent>

      {/* Expanded detail */}
      <Collapse in={effectiveExpanded}>
        <Box sx={{ px: 2, pb: 2 }}>
          {woQuery.isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {t('myTasks.loadingDetails')}
              </Typography>
            </Box>
          )}

          {wo && (
            <>
              {/* Lift & WO info bar */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{
                  mb: 1.5,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                }}
              >
                {liftName && (
                  <Chip
                    size="small"
                    label={`${t('myTasks.lift')}: ${liftName}`}
                    color="info"
                    variant="outlined"
                  />
                )}
                {wo.internalNotes && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flex: 1 }}
                    noWrap
                  >
                    {wo.internalNotes}
                  </Typography>
                )}
                <Tooltip title={t('myTasks.viewDetails')}>
                  <IconButton
                    size="small"
                    aria-label={t('myTasks.viewDetails')}
                    onClick={() => navigate(`/workorders/${workOrderId}`)}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>

              {/* My work section */}
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{ mb: 0.5, display: 'block', textTransform: 'uppercase' }}
              >
                {t('myTasks.myWork')}
              </Typography>

              {services.map((svc) => {
                const detail = getServiceDetail(svc.bookingServiceId);
                const laborItems = detail?.laborItems ?? [];

                return (
                  <Box
                    key={svc.bookingServiceId}
                    sx={{
                      mb: 1.5,
                      pl: 1,
                      borderLeft: 3,
                      borderColor:
                        statusColors[svc.status] ?? statusColors.pending,
                    }}
                  >
                    {/* Service header with actions */}
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" fontWeight={600}>
                          {svc.serviceName}
                        </Typography>
                        <Chip
                          size="small"
                          label={t(`serviceStatus.${svc.status}`, svc.status)}
                          sx={{
                            backgroundColor:
                              statusColors[svc.status] ?? statusColors.pending,
                            color: 'common.white',
                            fontWeight: 500,
                            height: 20,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Stack>
                      {renderServiceActions(svc)}
                    </Stack>

                    {/* Labor items checklist */}
                    {laborItems.length > 0 && (
                      <Box sx={{ mt: 0.5 }}>
                        {laborItems.map((item) => (
                          <FormControlLabel
                            key={item.id}
                            onClick={(e) => e.stopPropagation()}
                            disabled={
                              isLaborItemLocked(svc.workOrderStatus) ||
                              startLaborItemMut.isPending ||
                              completeLaborItemMut.isPending ||
                              updateLaborItemStatusMut.isPending
                            }
                            sx={{
                              ml: -0.5,
                              mr: 0,
                              display: 'flex',
                              minHeight: 44,
                              alignItems: 'center',
                            }}
                            control={
                              <Checkbox
                                size="small"
                                checked={item.status === 'completed'}
                                indeterminate={item.status === 'in_progress'}
                                onChange={() =>
                                  void handleLaborItemToggle(
                                    svc.bookingServiceId,
                                    item,
                                  )
                                }
                                sx={{ p: 0.75 }}
                              />
                            }
                            label={
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    textDecoration:
                                      item.status === 'completed'
                                        ? 'line-through'
                                        : 'none',
                                    color:
                                      item.status === 'completed'
                                        ? 'text.disabled'
                                        : 'text.primary',
                                  }}
                                >
                                  {item.description}
                                </Typography>
                                {item.estimatedHours > 0 && (
                                  <Typography variant="caption" color="text.disabled">
                                    {formatDuration(item.estimatedHours)}
                                  </Typography>
                                )}
                              </Stack>
                            }
                          />
                        ))}
                        {/* Labor progress bar */}
                        <LinearProgress
                          variant="determinate"
                          value={
                            (laborItems.filter((i) => i.status === 'completed')
                              .length /
                              laborItems.length) *
                            100
                          }
                          sx={{ mt: 0.5, borderRadius: 1, height: 3 }}
                        />
                      </Box>
                    )}

                  </Box>
                );
              })}

              {/* Once per work order — not per service, or the same panel repeats. */}
              <Box sx={{ mt: 1 }} onClick={(e) => e.stopPropagation()}>
                <MechanicPartsPanel
                  workOrderId={workOrderId}
                  collapsible
                />
              </Box>

              {/* Inline checklists panel */}
              <InlineChecklistPanel workOrderId={workOrderId} />

              {/* WorkPath tasks panel — rendered independent of service state so
                  verification progress stays visible across start/pause/complete. */}
              <WorkPathTasksPanel workOrderId={workOrderId} />

              {/* Other mechanics' work */}
              {otherServices.length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{
                      mt: 1,
                      mb: 0.5,
                      display: 'block',
                      textTransform: 'uppercase',
                    }}
                  >
                    {t('myTasks.otherWork')}
                  </Typography>

                  {otherServices.map((svc) => {
                    const svcLiftName = wo.assignedLiftId
                      ? liftsMap.get(wo.assignedLiftId)
                      : null;

                    return (
                      <Stack
                        key={svc.bookingServiceId}
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                          py: 0.5,
                          px: 1,
                          borderRadius: 1,
                          bgcolor: 'action.hover',
                          mb: 0.5,
                        }}
                      >
                        <PersonIcon
                          sx={{ fontSize: 16, color: 'text.secondary' }}
                        />
                        <Typography
                          variant="body2"
                          color="text.primary"
                          sx={{ flex: 1 }}
                          noWrap
                        >
                          {svc.mechanic
                            ? `${svc.mechanic.firstName} ${svc.mechanic.lastName}`
                            : '—'}
                        </Typography>
                        <Typography variant="body2" color="text.primary" noWrap>
                          {svc.name}
                        </Typography>
                        <Chip
                          size="small"
                          label={t(`serviceStatus.${svc.status}`, svc.status)}
                          sx={{
                            backgroundColor:
                              statusColors[svc.status] ?? statusColors.pending,
                            color: 'common.white',
                            fontWeight: 500,
                            height: 20,
                            fontSize: '0.7rem',
                          }}
                        />
                        {svcLiftName && (
                          <Typography variant="caption" color="text.secondary">
                            {svcLiftName}
                          </Typography>
                        )}
                      </Stack>
                    );
                  })}
                </>
              )}
            </>
          )}
        </Box>
      </Collapse>
    </Card>
    <MileageRequiredDialog
      open={mileageDialogOpen}
      onConfirm={(mileage) => { void handleMileageConfirm(mileage); }}
      onCancel={handleMileageCancel}
      loading={mileageDialogLoading}
    />
    </>
  );
}
